import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Deep-link focus for config pages (2026-08-11).
 *
 * The Rider Layout Editor links to config pages with `?focus=<fieldId>` (e.g.
 * `?focus=general-info.show-duration`). This hook reads that parameter, waits
 * for the page to be loaded, then scrolls to the matching element
 * (`[data-field-id="..."]`) and flashes a highlight so the user lands directly
 * on the field they clicked "edit" for.
 *
 * Usage: call it with a `ready` flag (e.g. `loaded` from the page's load
 * state). Elements opt in by setting `data-field-id={fieldId}`.
 */
export function useFocusField(ready: boolean): void {
  const [params] = useSearchParams();
  const focusId = params.get('focus');

  useEffect(() => {
    if (!focusId || !ready) return;
    const scrollTo = () => {
      const el = document.querySelector<HTMLElement>(`[data-field-id="${CSS.escape(focusId)}"]`);
      if (!el) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('field-focus-flash');
      window.setTimeout(() => el.classList.remove('field-focus-flash'), 1800);
      return true;
    };
    // First attempt lets the layout settle; a second attempt catches sections
    // that mount asynchronously (e.g. the intro text which loads from the API).
    const t1 = window.setTimeout(() => {
      if (!scrollTo()) {
        window.setTimeout(scrollTo, 900);
      }
    }, 250);
    return () => window.clearTimeout(t1);
  }, [focusId, ready]);
}
